import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Phone, MessageCircle, MapPin, Clock, Truck } from 'lucide-react';
import { KineticLines, FadeUp, Eyebrow } from '../components/motion';
import { QuoteForm } from '../components/QuoteForm';
import { PHONE_TEL, PHONE_DISPLAY, WHATSAPP_URL, CITIES } from '../data/content';

const Hero = () => (
  <section className="relative pt-40 pb-16 md:pt-48 md:pb-24 overflow-hidden">
    <div className="relative max-w-7xl mx-auto px-6 md:px-10">
      <FadeUp delay={0.1}>
        <Eyebrow>İletişim</Eyebrow>
      </FadeUp>
      <KineticLines
        className="mt-6 font-display font-medium uppercase tracking-tighter leading-[0.95] text-4xl sm:text-6xl lg:text-7xl"
        lines={[
          <span key="1" className="block">Bir telefonla</span>,
          <span key="2" className="block text-[#FF5C00]">yola çıkalım</span>,
        ]}
      />
      <FadeUp delay={0.6} className="mt-7 max-w-xl">
        <p className="text-[#A0ABC0] text-base md:text-lg leading-relaxed">
          Ücretsiz keşif ve net fiyat teklifi için bizi arayın ya da WhatsApp&apos;tan yazın.
        </p>
      </FadeUp>
    </div>
  </section>
);

const ContactCards = () => (
  <section data-testid="contact-cards" className="pb-24 md:pb-32">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-5">
      <FadeUp>
        <a
          href={PHONE_TEL}
          data-testid="contact-phone-card"
          className="card-hover group block h-full bg-[#FF5C00] rounded-2xl p-10 md:p-12"
        >
          <Phone size={32} strokeWidth={2.2} className="text-[#0A1128] mb-8 transition-transform duration-500 group-hover:rotate-12" />
          <span className="block text-xs uppercase tracking-[0.3em] text-[#0A1128]/70 mb-3">
            Telefon
          </span>
          <span className="block font-display font-semibold text-3xl md:text-5xl tracking-tighter text-[#0A1128]">
            {PHONE_DISPLAY}
          </span>
          <span className="block mt-4 text-[#0A1128]/80 text-sm">
            Haftanın 7 günü, mesai saatleri içinde bize ulaşabilirsiniz.
          </span>
        </a>
      </FadeUp>

      <FadeUp delay={0.12}>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-whatsapp-card"
          className="card-hover group block h-full bg-[#0A1128] border border-[#1E293B] hover:border-[#25D366]/60 rounded-2xl p-10 md:p-12"
        >
          <MessageCircle size={32} strokeWidth={2.2} className="text-[#25D366] mb-8" />
          <span className="block text-xs uppercase tracking-[0.3em] text-[#A0ABC0] mb-3">
            WhatsApp
          </span>
          <span className="block font-display font-semibold text-3xl md:text-4xl tracking-tighter text-[#F2F4F8]">
            Fotoğraf gönderin,
            <br />
            hızlı fiyat alın
          </span>
          <span className="block mt-4 text-[#A0ABC0] text-sm">
            Eşyalarınızın fotoğrafını atın, dakikalar içinde ön fiyat paylaşalım.
          </span>
        </a>
      </FadeUp>
    </div>
  </section>
);

const InfoBand = () => (
  <section data-testid="contact-info-band" className="py-24 md:py-32 bg-[#0A1128] border-y border-[#1E293B]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid sm:grid-cols-3 gap-5">
      {[
        {
          icon: MapPin,
          title: 'Merkez',
          desc: 'İnegöl / Bursa',
          sub: 'Türkiye geneli hizmet ağı',
        },
        {
          icon: Clock,
          title: 'Çalışma Saatleri',
          desc: 'Pazartesi — Pazar',
          sub: '08:00 – 20:00',
        },
        {
          icon: Truck,
          title: 'Hizmet Alanı',
          desc: '81 İl, 922 İlçe',
          sub: 'İnegöl çıkışlı ve varışlı',
        },
      ].map((item, i) => (
        <FadeUp key={item.title} delay={i * 0.1}>
          <div
            data-testid={`info-card-${i}`}
            className="h-full bg-[#060B19] border border-[#1E293B] rounded-2xl p-9"
          >
            <item.icon size={24} className="text-[#FF5C00] mb-6" />
            <span className="block text-[11px] uppercase tracking-[0.25em] text-[#A0ABC0] mb-2">
              {item.title}
            </span>
            <h3 className="font-display text-xl md:text-2xl tracking-tight">{item.desc}</h3>
            <p className="text-[#A0ABC0] text-sm mt-2">{item.sub}</p>
          </div>
        </FadeUp>
      ))}
    </div>

    <FadeUp className="max-w-7xl mx-auto px-6 md:px-10 mt-16">
      <p className="text-xs uppercase tracking-[0.3em] text-[#A0ABC0] mb-6">
        Popüler Güzergâhlarımız
      </p>
      <div className="flex flex-wrap gap-3">
        {CITIES.slice(0, 12).map((c, i) => (
          <span
            key={c}
            data-testid={`contact-route-chip-${i}`}
            className="px-4 py-2 rounded-full border border-[#1E293B] text-sm text-[#F2F4F8]/80 hover:border-[#FF5C00] hover:text-[#FF5C00] transition-colors duration-300 cursor-default"
          >
            İnegöl ⇄ {c}
          </span>
        ))}
        <span className="px-4 py-2 rounded-full text-sm text-[#A0ABC0]">ve 69 il daha…</span>
      </div>
    </FadeUp>
  </section>
);

const QuoteSection = () => (
  <section id="teklif-formu" data-testid="quote-section" className="pb-24 md:pb-32 scroll-mt-28">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-5 gap-12 items-start">
      <FadeUp className="lg:col-span-2">
        <Eyebrow>Online Teklif</Eyebrow>
        <h2 className="mt-5 font-display font-medium tracking-tight text-4xl md:text-5xl leading-tight">
          2 dakikada <span className="text-[#FF5C00]">teklif isteyin</span>
        </h2>
        <p className="mt-6 text-[#A0ABC0] leading-relaxed max-w-md">
          Formu doldurun, ekibimiz aynı gün içinde sizi arayıp net fiyatınızı paylaşsın. Keşif
          her zaman ücretsizdir.
        </p>
        <ul className="mt-8 space-y-3 text-sm text-[#F2F4F8]/85">
          {['Aynı gün geri dönüş', 'Sabit ve net fiyat', 'Ücretsiz ekspertiz'].map((t) => (
            <li key={t} className="flex items-center gap-3">
              <span className="w-1.5 h-1.5 rotate-45 bg-[#FF5C00]" /> {t}
            </li>
          ))}
        </ul>
      </FadeUp>
      <FadeUp delay={0.15} className="lg:col-span-3">
        <QuoteForm />
      </FadeUp>
    </div>
  </section>
);

export default function IletisimPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash === '#teklif-formu') {
      setTimeout(() => {
        document.getElementById('teklif-formu')?.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    }
  }, [hash]);

  return (
    <main data-testid="contact-page">
      <Hero />
      <QuoteSection />
      <ContactCards />
      <InfoBand />
    </main>
  );
}
